"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

// =============================================================================
// RadioGroup
// =============================================================================

export interface RadioGroupProps {
  /** Currently selected value */
  value: string | null;
  /** Callback when a new value is selected */
  onValueChange: (value: string) => void;
  /** Group name attribute for the native radio inputs */
  name?: string;
  /** Orientation of the group */
  orientation?: "horizontal" | "vertical";
  /** Whether the group is disabled */
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

const RadioGroupContext = React.createContext<{
  value: string | null;
  name: string;
  disabled: boolean;
  onValueChange: (value: string) => void;
} | null>(null);

function useRadioGroupContext() {
  const ctx = React.useContext(RadioGroupContext);
  if (!ctx) {
    throw new Error("RadioItem must be used within a RadioGroup");
  }
  return ctx;
}

function RadioGroup({
  value,
  onValueChange,
  name = "radio-group",
  orientation = "vertical",
  disabled = false,
  className,
  children,
}: RadioGroupProps) {
  const contextValue = React.useMemo(
    () => ({ value, name, disabled, onValueChange }),
    [value, name, disabled, onValueChange]
  );

  return (
    <RadioGroupContext.Provider value={contextValue}>
      <div
        role="radiogroup"
        aria-orientation={orientation}
        className={cn(
          "flex",
          orientation === "vertical" ? "flex-col gap-3" : "flex-row gap-4",
          className
        )}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

// =============================================================================
// RadioItem
// =============================================================================

export interface RadioItemProps {
  /** Value for this option */
  value: string;
  /** Whether this specific item is disabled */
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

function RadioItem({
  value,
  disabled: itemDisabled = false,
  className,
  children,
}: RadioItemProps) {
  const group = useRadioGroupContext();
  const isChecked = group.value === value;
  const isDisabled = group.disabled || itemDisabled;
  const id = `${group.name}-${value}`;

  return (
    <label
      htmlFor={id}
      className={cn(
        "flex items-center gap-2.5 cursor-pointer text-sm text-text-primary",
        isDisabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      <span className="relative flex items-center justify-center shrink-0">
        <input
          type="radio"
          id={id}
          name={group.name}
          value={value}
          checked={isChecked}
          disabled={isDisabled}
          onChange={() => group.onValueChange(value)}
          className="sr-only peer"
        />
        <span
          className={cn(
            "h-4 w-4 rounded-full border transition-colors",
            "peer-focus-visible:ring-2 peer-focus-visible:ring-slate-800/20 peer-focus-visible:ring-offset-1",
            isChecked
              ? "border-slate-800 bg-slate-800"
              : "border-border-primary bg-white",
            !isDisabled && !isChecked && "hover:border-slate-600"
          )}
        />
        {isChecked && (
          <span className="absolute h-1.5 w-1.5 rounded-full bg-white" />
        )}
      </span>
      {children}
    </label>
  );
}

export { RadioGroup, RadioItem };
